import { createFeature, createReducer, on } from '@ngrx/store'
import { ClockInClockOutState } from './clockIn-clockOut.state'
import {
  clearAttendanceState,
  createClockInClockOut,
  createClockInClockOutFailure,
  createClockInClockOutSuccess,
  createEvent,
  createEventSuccess,
  createEventFailure,
  getLatestClockInFailure,
  getLatestClockInSuccess,
  loadAttendances,
  loadAttendancesFailure,
  loadAttendancesSuccess,
  loadEmployeeAttendanceFailure,
  loadEmployeeAttendanceSuccess,
  updateClockIn,
  updateClockInFailure,
  updateClockInSuccess,
  clearLatestPunchIn
} from './clockIn-clockOut.actions'

export const initialState: ClockInClockOutState = {
  clockinclockouts: [],
  adminData: {
    totalPresentToday: 0,
    lateLoginsToday: 0,
    todayPlannedLeaves: 0,
    todayUnplannedLeaves: 0,
    absentToday: 0,
    employeeCount: 0
  },
  loading: false,
  error: null,
  isPunchedIn: false,
  latestPunchIn: null,
  totalCount: 0,
  totalWorkHoursThisWeek: '',
  totalWorkHoursThisMonth: '',
  totalWorkHoursToday: '',
  totalProductiveWorkHoursToday: '',
  totalBreakHoursToday: '0h 0m',
  todaysClockinoutRecords: [],
  expectedWorkHoursToday: '0h',
  expectedWorkHoursThisWeek: '0h',
  expectedWorkHoursThisMonth: '0h',
  totalOvertimeToday: '0h 0m',
  totalOvertimeThisMonth: '0h 0m'
}

export const clockInClockOutReducer = createReducer(
  initialState,
  on(loadAttendances, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    loadAttendancesSuccess,
    (
      state,
      {
        totalPresentToday,
        lateLoginsToday,
        todayPlannedLeaves,
        todayUnplannedLeaves,
        absentToday,
        employeeCount,
        totalCount,
        clockinclockouts
      }
    ) => ({
      ...state,
      adminData: {
        ...state.adminData,
        totalPresentToday,
        lateLoginsToday,
        todayPlannedLeaves,
        todayUnplannedLeaves,
        absentToday,
        employeeCount
      },
      totalCount,
      clockinclockouts: [...clockinclockouts],
      loading: false,
      error: null
    })
  ),
  on(loadAttendancesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(
    loadEmployeeAttendanceSuccess,
    (
      state,
      {
        totalCount,
        attendances,
        totalWorkHoursThisMonth,
        totalWorkHoursThisWeek,
        totalWorkHoursToday,
        totalProductiveWorkHoursToday,
        totalBreakHoursToday,
        todaysClockinoutRecords,
        expectedWorkHoursToday,
        expectedWorkHoursThisWeek,
        expectedWorkHoursThisMonth,
        totalOvertimeToday,
        totalOvertimeThisMonth
      }
    ) => ({
      ...state,
      clockinclockouts: attendances,
      totalCount: totalCount,
      totalWorkHoursThisMonth: totalWorkHoursThisMonth,
      totalWorkHoursThisWeek: totalWorkHoursThisWeek,
      totalWorkHoursToday: totalWorkHoursToday,
      todaysClockinoutRecords: todaysClockinoutRecords,
      totalProductiveWorkHoursToday: totalProductiveWorkHoursToday,
      totalBreakHoursToday: totalBreakHoursToday,
      expectedWorkHoursToday,
      expectedWorkHoursThisWeek,
      expectedWorkHoursThisMonth,
      totalOvertimeToday: totalOvertimeToday,
      totalOvertimeThisMonth: totalOvertimeThisMonth,

      error: null
    })
  ),
  on(loadEmployeeAttendanceFailure, (state, { error }) => ({
    ...state,
    attendaces: null,
    error
  })),
  on(createClockInClockOut, state => ({ ...state, loading: true })),
  on(createClockInClockOutSuccess, (state, { clockInClockOut }) => ({
    ...state,
    attendances: [...state.clockinclockouts, clockInClockOut],
    isPunchedIn: true,
    loading: false,
    latestPunchIn: clockInClockOut
  })),
  on(createClockInClockOutFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(getLatestClockInSuccess, (state, { isPunchedIn, latestPunchIn }) => {
    // console.log('isPunchedInstate', isPunchedIn)
    return {
      ...state,
      isPunchedIn: isPunchedIn,
      latestPunchIn: latestPunchIn
    }
  }),
  on(getLatestClockInFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(updateClockIn, state => ({ ...state, loading: true })),
  on(updateClockInSuccess, (state, { clockInClockOut }) => ({
    ...state,
    attendances: state.clockinclockouts.map(clockIn =>
      clockIn.id === clockInClockOut.id ? clockInClockOut : clockIn
    ),
    isPunchedIn: false,
    loading: false
  })),
  on(updateClockInFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(clearAttendanceState, state => ({
    ...state,
    clockinclockouts: [], // clear attendance array or object
    attendances: [],
    loading: false,
    error: null
  })),
  on(createEvent, state => ({ ...state, loading: true })),
  on(createEventSuccess, (state, { clockInClockOut }) => ({
    ...state,
    attendances: [],

    loading: false
  })),
  on(createEventFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(clearLatestPunchIn, state => ({
    ...state,
    latestPunchIn: null,
    isPunchedIn: false
  }))
)

// Create the feature for the Designation state
export const clockInClockOutFeature = createFeature({
  name: 'clockInClockOut', // Feature name
  reducer: clockInClockOutReducer
})

export type { ClockInClockOutState }
// Export the feature's selectors
export const {
  name,
  reducer,
  selectLoading,
  selectError,
  selectIsPunchedIn,
  selectClockinclockouts,
  selectTotalWorkHoursThisMonth,
  selectTotalWorkHoursThisWeek,
  selectTodaysClockinoutRecords,
  selectTotalBreakHoursToday,
  selectLatestPunchIn,
  selectAdminData,
  selectTotalCount,
  selectClockInClockOutState
} = clockInClockOutFeature
